<script setup lang="ts">
import { computed, provide, ref, toValue, watch } from "vue";
import { type RouteLocationRaw, useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import { hasConfigurableRegistrationEnabled } from "@/composables/authorization.js";
import Page404 from "@/university/views/404Page.vue";
import AuthGuardWithAccountCreation from "./AuthGuardWithAccountCreation/index.vue";
import useApplication from "./composables/useApplication";
import { useApplicationRouting } from "./composables/useApplicationRouting";
import { useExistingApplicationRedirect } from "./composables/useExistingApplicationRedirect";
import { usePastApplicationDeadline } from "./composables/usePastApplicationDeadline";
import { useRegistrationOptions } from "./composables/useRegistrationOptions";
import { LOGIN_REDIRECT_QUERY } from "./constants";
import RegistrationErrors from "./RegistrationErrors/index.vue";
import RegistrationLoading from "./RegistrationLoading/index.vue";
import SessionDeadlineModal from "./SessionDeadlineModal/index.vue";

const route = useRoute();
const toast = useToast();
const { isLegacy, programPageId, sessionId, applicationId } = useRegistrationOptions();

const isRedirectingFromLogin = computed<boolean>(() => Boolean(route.query?.[LOGIN_REDIRECT_QUERY]));

const pastDeadlineRedirect = computed<RouteLocationRaw>(() => {
  return { name: "results" };
});

const applicationInitializationParams = computed(() => {
  if (isLegacy.value) {
    return undefined;
  }
  if (route.name === "modify-application") {
    return { applicationId };
  } else if (route.name === "new-application") {
    return {
      sessionId,
      programPageId,
    };
  }
});

const application: Record<string, any> = useApplication(applicationInitializationParams);
const { loadingErrors, isLoading, mutationErrors, isMutating, hasPermissionErrors, hasApplicationErrors } =
  application;

const isApplicationLoading = computed<boolean>(
  () => toValue(isLoading) || toValue(isMutating) || toValue(isRedirectingFromLogin),
);

const loadingMessage = ref("Loading registration.  Please wait.");
provide("setRegistrationLoadingMessage", (msg: string) => {
  loadingMessage.value = msg;
});

const { isPastDeadline } = usePastApplicationDeadline(application);
const existingApplication = useExistingApplicationRedirect();
const { stage, step, component, renderComponent } = useApplicationRouting(application, existingApplication);

const show404 = computed(() => !hasConfigurableRegistrationEnabled.value);

const showComponent = computed(
  () => !show404.value && renderComponent.value && !(isApplicationLoading.value || hasPermissionErrors.value),
);

watch(hasApplicationErrors, (newVal) => {
  if (newVal) {
    toast.open({
      message: `
        <div class="flex items-center justify-between gap-3">
          <span>Something went wrong. Please try again. If this problem persists, please contact productsupport@apiexperience.com.</span>
          <button class="hover:opacity-70">
             <svg
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `,
      type: "error",
      position: "bottom",
      duration: 0, // User must dismiss
    });
  }
});
</script>
<template>
  <Page404 v-if="show404" />
  <registration-loading v-else-if="isApplicationLoading" :is-loading="isApplicationLoading" :message="loadingMessage" />
  <registration-errors v-else-if="hasPermissionErrors" :associated-order="application.primaryOrder" :loading-errors="loadingErrors" :mutation-errors="mutationErrors" />
  <auth-guard-with-account-creation v-show="showComponent" :session="application.session">
    <session-deadline-modal v-model="isPastDeadline" :navigate-to="pastDeadlineRedirect" />
    <component
      :is="component"
      v-bind="{ application, stage, step }"
    />
  </auth-guard-with-account-creation>
</template>
